import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment.dev';
import { BaseService } from '../../../@core/services/base.service';
import { IAttachment, IFile, IProject, IUser } from 'src/app/@core/models/model';
import { createExternalRequestContext } from '../../../@core/utils/http-context.utils';

export interface GeotechnicalAnalysisCreateResponse {
    _id: string;
    displayName?: string;
    [key: string]: unknown;
}

export interface IGeotechnicalAnalysis {
    readonly _id: string;
    name?: string | null;
    analysisNumber: number;
}

/** Filters for list endpoint */
export interface GeotechnicalAnalysisListFilters {
    searchValue?: string;
    projectId?: string;
    createdBy?: string[];
    dueDateStart?: string;
    dueDateEnd?: string;
    dateCreatedStart?: string;
    dateCreatedEnd?: string;
}

/** Request body for POST /list */
export interface GeotechnicalAnalysisListRequest {
    skip?: number;
    limit?: number;
    filters?: GeotechnicalAnalysisListFilters;
}

/** Single analysis item as returned by list aggregation (projected fields) */
export interface GeotechnicalAnalysisListItem {
    _id: string;
    name?: string;
    analysisNumber?: number;
    project?: string | IProject;
    projectName?: string; // populated when backend joins project
    dueDate?: string;
    createdAt?: string;
    updatedAt?: string;
    displayName?: string;
    upcomingMilestone?: { name?: string; date?: string };
    teamMembers?: Array<{ _id?: string; user?: { _id?: string; firstName?: string; lastName?: string }; name?: string; email?: string; role?: string }>;
    lastEditedOn?: string;
    lastEditedBy?: { _id?: string; firstName?: string; lastName?: string };
    currentUserRole?: string;
    status: string
}


export interface GeotechnicalAnalysisReport {
    _id: string;
    name?: string;
    analysisNumber?: number;
    analysisExcelUrl?: string | null;
    project?: IProject;
    projectName?: string; // populated when backend joins project
    dueDate?: string;
    createdAt?: string;
    updatedAt?: string;
    displayName?: string;
    reports?: Array<{ _id?: string; name?: string; type?: string; file?: IFile, attachment?: IAttachment, url?: string }>;
    formulae?: { file?: IFile; attachment?: IAttachment; source?: string; size?: number } | null;
    analysisExcel?: { file?: IFile; attachment?: IAttachment; source?: string; size?: number } | null;
    upcomingMilestone?: { name?: string; date?: string };
    teamMembers?: Array<{ _id?: string; user?: { _id?: string; firstName?: string; lastName?: string }; name?: string; email?: string }>;
    lastEditedOn?: string;
    lastEditedBy?: { _id?: string; firstName?: string; lastName?: string };
    createdBy?: { _id?: string; firstName?: string; lastName?: string };
    organization?: string;
    reportSummaryInstructions?: string | null;
    analysisSummaryInstructions?: string | null;
    aiDashboardAttachment?: string | null;
}

/** Response data for list endpoint */
export interface GeotechnicalAnalysisListResponse {
    analyses: GeotechnicalAnalysisListItem[];
    totalRecords: number;
}

/** Document info in a generated analysis excel (from GET /generate-analysis-excel). */
export interface GeneratedAnalysisExcelDocumentInfo {
    _id: string;
    name: string;
    type: string;
}

/** Single item from GET /generate-analysis-excel list. */
export interface GeneratedAnalysisExcelListItem {
    _id: string;
    name: string;
    analysis: { _id: string; filename: string; type: string; signedUrl: string } | null;
    documents: GeneratedAnalysisExcelDocumentInfo[];
    createdAt: string;
    updatedAt: string;
}

/** Input ref shape used in user preferences. */
export interface GeoPreferenceInputRef {
    attachment?: string;
    file?: string;
    source: string;
}

/** Resolved default entry from GET /user-preferences. */
export interface GeoPreferenceEntry {
    ref: GeoPreferenceInputRef;
    name: string;
    attachmentId: string;
}

/** Response from GET /user-preferences. */
export interface UserGeotechnicalPreference {
    defaultFormulae: GeoPreferenceEntry | null;
    defaultAnalysisExcel: GeoPreferenceEntry | null;
}

/** Response data for GET /generate-analysis-excel. */
export interface GeneratedAnalysisExcelListResponse {
    items: GeneratedAnalysisExcelListItem[];
    total: number;
}

/** Status from get-analysis-file (getAnalysisFileSignedUrls). */
export type GenerateAnalysisExcelStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CREDITS_EXHAUSTED';

/** Response data for GET /get-analysis-file (signed URL for the generated file). */
export interface GetAnalysisFileResponse {
    awsSignedUrl: string | null;
    status: GenerateAnalysisExcelStatus;
    attachment: IAttachment | null;
}

/** Response data for GET /fetch-latest-summary/:analysisId (insights summary). */
export interface FetchLatestSummaryResponse {
    summary: string;
    needsNewSummary: boolean;
    _id: string
    summaryStatus?: GEOTECHNICAL_SUMMARY_STATUS;
    analysisSummaryStatus?: GEOTECHNICAL_SUMMARY_STATUS;
    lastUserInstructions?: string | null;
    workspaceInstructions?: string | null;
}

/** Item from GET /summary-list/:analysisId (insights summary history). */
export interface GeotechnicalAnalysisSummaryListItem {
    _id?: string;
    name?: string;
    createdAt?: string;
    createdBy?: IUser;
    [key: string]: unknown;
}

/** Response data for the summary History "View"/"Restore" content endpoints. */
export interface GeotechnicalSummaryContent {
    _id: string;
    name: string | null;
    summary: string;
    createdAt: string;
}

/** A single AI dashboard version returned by the versions list endpoint. */
export interface IAiDashboardVersion {
    _id: string;
    name: string;
    versionNumber: number;
    userInstructions: string | null;
    createdBy: IUser;
    createdAt: string;
    updatedAt: string;
    isCurrentVersion?: boolean;
    /** Attachment ID — used to match against GeotechnicalAnalysis.aiDashboardAttachment */
    attachment?: string;
}

/**
 * Lifecycle statuses emitted by the GEOTECHNICAL_SUMMARY_STATUS_CHANGED socket event.
 * Mirrors the backend enum so the frontend can react to each transition.
 */
export enum GEOTECHNICAL_SUMMARY_STATUS {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CREDITS_EXHAUSTED = 'CREDITS_EXHAUSTED',
}

export enum FORMULA_DASHBOARD_STATUS {
    NOT_STARTED = 'NOT_STARTED',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CREDITS_EXHAUSTED = 'CREDITS_EXHAUSTED',
}

export enum DASHBOARD_SECTION_TYPE {
    FORMULA = 'FORMULA',
    INDEPENDENT_PARAMETERS = 'INDEPENDENT_PARAMETERS',
}

export enum FORMULA_PARAMETER_SOURCE_TYPE {
    ANALYSIS_EXCEL = 'ANALYSIS_EXCEL',
    AI_EXTRACTED = 'AI_EXTRACTED',
}

export interface IFormulaParameterRef {
    _id: string;
    nameKey: string;
    label: string;
    description?: string | null;
}

export interface IFormulaDashboardSection {
    _id: string;
    title: string;
    type: DASHBOARD_SECTION_TYPE;
    formula: string | null;
    parameters: IFormulaParameterRef[];
}

export interface IComputedParameterValue {
    nameKey: string;
    value?: string | null;
    unit?: string | null;
    reasoning?: string | null;
    sourceType?: FORMULA_PARAMETER_SOURCE_TYPE | null;
    source?: string[];
}

export interface IComputedFormulaResult {
    sectionId: string;
    label?: string | null;
    value?: string | null;
    unit?: string | null;
}

export interface IGeotechnicalFormulaDashboard {
    geotechnicalAnalysis: string | GeotechnicalAnalysisReport;
    status: FORMULA_DASHBOARD_STATUS;
    error?: string | null;
    sections: IFormulaDashboardSection[];
    parameterValues: IComputedParameterValue[];
    formulaResults: IComputedFormulaResult[];
    createdBy: string | IUser;
    createdAt: string;
    updatedAt: string;
}

export interface GeotechnicalUploadFile {
    fieldName: 'reportFiles' | 'formulaFile' | 'analysisExcelFile';
    fileName: string;
    contentType: string;
}

export interface GeotechnicalPresignedUrl {
    fieldName: string;
    fileName: string;
    uploadUrl: string;
    s3Key: string;
}

export interface GeotechnicalPresignedUrlsResponse {
    urls: GeotechnicalPresignedUrl[];
}




// ── Value Engineering interfaces ─────────────────────────────────────────────

export enum VALUE_ENGINEERING_STATUS {
    NOT_STARTED = 'NOT_STARTED',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CREDITS_EXHAUSTED = 'CREDITS_EXHAUSTED',
}

export interface IStabilizationMethodCell {
    key: string;
    value: string | null;
}

export enum PARAMETER_TYPES {
    DEPTH = "DEPTH",
    NORMAL = "NORMAL"
}

export interface IAlternativeValue {
    layer: string;
    parameter: string;
    value: string | null;
    type: PARAMETER_TYPES
    method: string[];
    status: VALUE_ENGINEERING_STATUS;
}

export interface IAlternative {
    key: string;
    values: IAlternativeValue[];
    isCustomAlternative: boolean;
    suitable: boolean;
}

export interface IGeotechnicalValueEngineering {
    _id: string;
    geotechnicalAnalysis: string;
    status: VALUE_ENGINEERING_STATUS;
    error?: string | null;
    stabilizationMethods: IStabilizationMethodCell[][];
    alternatives: IAlternative[];
    createdBy: string | IUser;
    createdAt: string;
    updatedAt: string;
}

// ── Vendor Analysis interfaces ────────────────────────────────────────────────

export enum VENDOR_ANALYSIS_STATUS {
    NOT_STARTED = 'NOT_STARTED',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CREDITS_EXHAUSTED = 'CREDITS_EXHAUSTED',
}

export enum IVendorSourceType {
    WEB_SEARCH = 'WEB_SEARCH',
    SHEET = 'SHEET',
}

export interface IVendorProjectSource {
    reason: string | null;
    sourceType: string | null;
    sources: string[];
}

export interface IVendorProjectField {
    value: number | null;
    source: IVendorProjectSource;
    status: string | null;
}

export interface IVendorProjectInfo {
    projectArea: IVendorProjectField;
    shiftHoursPerDay: IVendorProjectField;
    theoreticalBudget: IVendorProjectField;
    theoreticalScheduleWorkdays: IVendorProjectField;
    projectLocation: IVendorProjectField;
}

export interface IVendorField<T = string | number | null> {
    value: T;
    source: IVendorProjectSource;
}

export interface IGeneralWork {
    _id?: string;
    name: IVendorField<string | null>;
    description: IVendorField<string | null>;
    productionRateUnit: IVendorField<string | null>;
    productionRate: IVendorField<number | null>;
}

export interface IStabilizationWork {
    _id?: string;
    name: IVendorField<string | null>;
    description: IVendorField<string | null>;
    productionRateUnit: IVendorField<string | null>;
    productionRate: IVendorField<number | null>;
}

export interface IGeotechnicalVendorAnalysis {
    _id: string;
    geotechnicalAnalysis: string;
    status: VENDOR_ANALYSIS_STATUS;
    error?: string | null;
    projectInfo: IVendorProjectInfo;
    generalWorks: IGeneralWork[];
    stabilizationWorks: IStabilizationWork[];
    generalWorkCosts: IWorkCostEntry[];
    stabilizationWorkCosts: IWorkCostEntry[];
    createdBy: string | IUser;
    createdAt: string;
    updatedAt: string;
}

// ── Work Costs ─────────────────────────────────────────────────────────────────

export interface IWorkCostMaterial {
    material: IVendorField<string | null>;
    wasteFactor: IVendorField<number | null>;
    unitWeight: IVendorField<number | null>;
    unitCost: IVendorField<number | null>;
    taxOtPercent: IVendorField<number | null>;
}

export interface IWorkCostEquipment {
    equipment: IVendorField<string | null>;
    unitCostPerHr: IVendorField<number | null>;
    taxOtPercent: IVendorField<number | null>;
    quantity: IVendorField<number | null>;
}

export interface IWorkCostLabour {
    labour: IVendorField<string | null>;
    unitCostPerHr: IVendorField<number | null>;
    taxOtPercent: IVendorField<number | null>;
    quantity: IVendorField<number | null>;
}

export interface IWorkCostEntry {
    workName: IVendorField<string | null>;
    materials: IWorkCostMaterial[];
    equipments: IWorkCostEquipment[];
    labours: IWorkCostLabour[];
}

// ── Alternate Leaderboard interfaces ─────────────────────────────────────────

export interface ILeaderboardFilters {
    layers?: string[];
    rankBy?: 'cost' | 'schedule';
    topN?: number | null;
}

export interface IDashboardFilters {
    rankBy?: 'cost' | 'schedule';
    topN?: number | null;
    enabledLayers?: string[];
    selectedAlternates?: string[];
}

export interface IAlternateLeaderboardWorkItem {
    workId: string;
    workName: string;
    cost: number;
    material: number;
    labor: number;
    equipment: number;
}

export interface IAlternateLeaderboardItem {
    key: string;
    totalCost: { currency: string; value: number };
    costMix: { material: number; equipment: number; labor: number };
    scheduleImpact: number;
    scheduleChangePercent: number;
    generalWorksCost: number;
    stabilizationWorksCost: number;
    totalUnitCost: number;
    generalWorksBreakdown: IAlternateLeaderboardWorkItem[];
    stabilizationWorksBreakdown: IAlternateLeaderboardWorkItem[];
}


export enum IAlternateLeaderboardStatus {
    COMPLETED = "COMPLETED",
    PROCESSING = "PROCESSING",
    FAILED = "FAILED",
    NOT_STARTED = "NOT_STARTED"
}

export enum GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE {
    EXAMPLE_EXCEL = 'EXAMPLE_EXCEL',
    STANDARD_EXCEL = 'STANDARD_EXCEL',
    EXAMPLE_FORMULA = 'EXAMPLE_FORMULA',
    STANDARD_FORMULA = 'STANDARD_FORMULA',
}

export interface IAlternateLeaderboardResponse {
    alternates: IAlternateLeaderboardItem[];
    status: IAlternateLeaderboardStatus
}

// ── Calculate Costs for New Alternate interfaces ──────────────────────────────

export interface ICalculateCostsForNewAlternateLayerMethod {
    layer: string;
    parameter: string; // stabilization work name
    value: string;     // selected method value
}

export interface ICalculateCostsForNewAlternateRequest {
    currentAlternate: string;
    layerMethods: ICalculateCostsForNewAlternateLayerMethod[];
}


export interface ILayerVisualizationStabilizationMethod {
    label: string;
    value: string;
}

export interface ILayerVisualizationCell {
    depth: string;
    stabilizationMethods: ILayerVisualizationStabilizationMethod[];
}

export interface ILayerVisualizationRow {
    layerName: string;
    cells: ILayerVisualizationCell[];
}

export interface ILayerVisualizationData {
    alternateKeys: string[];
    suitableAlternateKey: string | null;
    rows: ILayerVisualizationRow[];
}


/**
 * Service for geotechnical analysis API.
 */
@Injectable({ providedIn: 'root' })
export class GeotechnicalAnalysisService extends BaseService {
    apiUrl = environment.apiUrl + '/geotechnical-analysis';

    constructor(protected override http: HttpClient) {
        super(http);
    }

    /**
     * Get pre-signed S3 upload URLs for local files.
     */
    getUploadPresignedUrls(files: GeotechnicalUploadFile[]) {
        return this.post<GeotechnicalPresignedUrlsResponse>(`${this.apiUrl}/upload-presigned-urls`, { files });
    }

    /**
     * Upload a file directly to S3 using a pre-signed URL.
     */
    uploadToS3(presignedUrl: string, file: File) {
        return this.http.put(presignedUrl, file, {
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            context: createExternalRequestContext(),
        });
    }

    /**
     * Create and save a geotechnical analysis.
     * Sends FormData with 'data' as JSON string (s3Keys included) — no file streams.
     */
    createAndSave(payload: object) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(payload));
        return this.post<GeotechnicalAnalysisCreateResponse>(`${this.apiUrl}`, formData);
    }

    /**
     * Update an existing geotechnical analysis.
     * Sends FormData with 'data' as JSON string (s3Keys included) — no file streams.
     */
    updateAndSave(id: string, payload: object) {
        const formData = new FormData();
        formData.append('data', JSON.stringify(payload));
        return this.put<GeotechnicalAnalysisCreateResponse>(`${this.apiUrl}/${id}`, formData);
    }

    /**
     * Generate analysis excel (separate endpoint). FormData: optional 'data' (JSON with analysisExcels refs only) and analysisExcelFile(s).
     */
    generateAnalysisExcel(formData: FormData) {
        return this.post<GeotechnicalAnalysisCreateResponse>(`${this.apiUrl}/generate-analysis-excel`, formData);
    }

    /**
     * List generated analysis excels (GET /generate-analysis-excel?skip=&limit=).
     */
    listGeneratedAnalysisExcels(params: { skip?: number; limit?: number } = {}) {
        const skip = Math.max(0, params.skip ?? 0);
        const limit = Math.min(100, Math.max(1, params.limit ?? 20));
        return this.get<GeneratedAnalysisExcelListResponse>(`${this.apiUrl}/generate-analysis-excel`, {
            skip: String(skip),
            limit: String(limit),
        });
    }

    /**
     * Get analysis file (GET /get-analysis-file). Returns signed URL and status.
     */
    getAnalysisFile(analysisId: string) {
        return this.get<GetAnalysisFileResponse>(`${this.apiUrl}/get-excel-analysis-file`, {
            id: analysisId,
        });
    }

    /**
     * Update a generated analysis excel name (PUT /edit-excel-analysis). Body: id + name.
     */
    editExcelAnalysis(body: { id: string; name: string }) {
        return this.put<GeneratedAnalysisExcelListItem>(`${this.apiUrl}/edit-excel-analysis`, body);
    }

    /**
     * Delete a generated analysis excel (DELETE /delete-analysis).
     */
    deleteAnalysis(analysisId: string) {
        return this.delete<{ success?: boolean }>(`${this.apiUrl}/generate-analysis-excel/${analysisId}`);
    }

    /**
     * Get user geotechnical preferences (default formulae + default analysis excel).
     */
    getUserPreferences() {
        return this.get<UserGeotechnicalPreference>(`${this.apiUrl}/user-preferences`);
    }

    /**
     * Upsert user geotechnical preferences. Pass null to clear a default.
     */
    updateUserPreferences(body: { defaultFormulae?: GeoPreferenceInputRef | null; defaultAnalysisExcel?: GeoPreferenceInputRef | null }) {
        return this.put<void>(`${this.apiUrl}/user-preferences`, body);
    }

    /**
     * Fetch geotechnical analyses for the current workspace with optional pagination and filters.
     */
    list(params: GeotechnicalAnalysisListRequest) {
        return this.post<GeotechnicalAnalysisListResponse>(`${this.apiUrl}/list`, {
            skip: params.skip ?? 0,
            limit: params.limit ?? 20,
            filters: params.filters ?? {},
        });
    }

    /**
     * Fetch geotechnical analysis report by ID.
     */
    getReport(id: string) {
        return this.get<GeotechnicalAnalysisReport>(`${this.apiUrl}/${id}`);
    }

    /**
     * Fetch latest insights summary for a geotechnical analysis (GET /fetch-latest-summary/:analysisId).
     * Returns summary text and whether a new summary is needed.
     */
    fetchLatestSummary(analysisId: string) {
        return this.get<FetchLatestSummaryResponse>(`${this.apiUrl}/fetch-latest-summary/${analysisId}`);
    }

    reGenerateSummary(id: string, description?: string) {
        return this.post<unknown>(`${this.apiUrl}/re-generate-summary`, { id, description: description ?? '' });
    }

    /**
     * Fetch summary list for an analysis (GET /summary-list/:analysisId).
     */
    summaryList(analysisId: string) {
        return this.get<GeotechnicalAnalysisSummaryListItem[]>(`${this.apiUrl}/summary-list/${analysisId}`);
    }

    /**
     * Download summary file (GET /download-summary/:analysisId). Returns blob response with file.
     */
    downloadSummary(analysisId: string) {
        return this.http.get(`${this.apiUrl}/download-summary/${analysisId}`, {
            responseType: 'blob',
            observe: 'response',
        });
    }

    /**
     * Download a specific summary by id (GET /download-summary/:summaryId). Returns blob response with file.
     */
    downloadSummaryById(summaryId: string) {
        return this.http.get(`${this.apiUrl}/download-summary/${summaryId}`, {
            responseType: 'blob',
            observe: 'response',
        });
    }

    /**
     * Delete a summary (DELETE /delete-summary/:summaryId).
     */
    deleteSummary(summaryId: string) {
        return this.delete<{ success?: boolean }>(`${this.apiUrl}/delete-summary/${summaryId}`);
    }

    /**
     * Fetch a single summary's markdown content for the History "View" action
     * (GET /summary-content/:summaryId).
     */
    getSummaryContentById(summaryId: string) {
        return this.get<GeotechnicalSummaryContent>(`${this.apiUrl}/summary-content/${summaryId}`);
    }

    /**
     * Rename a summary history entry (PUT /rename-summary/:summaryId).
     */
    renameSummary(summaryId: string, name: string) {
        return this.put<GeotechnicalAnalysisSummaryListItem>(`${this.apiUrl}/rename-summary/${summaryId}`, { name });
    }

    /**
     * Restore an older summary as the current one (POST /restore-summary/:summaryId).
     * Returns the restored summary's content so the main view can refresh.
     */
    restoreSummary(summaryId: string) {
        return this.post<GeotechnicalSummaryContent>(`${this.apiUrl}/restore-summary/${summaryId}`, {});
    }

    // ── Analysis Summary ─────────────────────────────────────────────

    /**
     * Fetch latest analysis summary for a geotechnical analysis (GET /fetch-latest-analysis-summary/:analysisId).
     */
    fetchLatestAnalysisSummary(analysisId: string) {
        return this.get<FetchLatestSummaryResponse>(`${this.apiUrl}/fetch-latest-analysis-summary/${analysisId}`);
    }

    regenerateAnalysisSummary(id: string, description?: string) {
        return this.post<unknown>(`${this.apiUrl}/re-generate-analysis-summary`, { id, description: description ?? '' });
    }

    /**
     * Fetch analysis summary list for an analysis (GET /analysis-summary-list/:analysisId).
     */
    analysisSummaryList(analysisId: string) {
        return this.get<GeotechnicalAnalysisSummaryListItem[]>(`${this.apiUrl}/analysis-summary-list/${analysisId}`);
    }

    /**
     * Download latest analysis summary file (GET /download-latest-analysis-summary/:analysisId). Returns blob response with file.
     */
    downloadAnalysisSummary(analysisId: string) {
        return this.http.get(`${this.apiUrl}/download-latest-analysis-summary/${analysisId}`, {
            responseType: 'blob',
            observe: 'response',
        });
    }

    /**
     * Download a specific analysis summary by id (GET /download-analysis-summary/:summaryId). Returns blob response with file.
     */
    downloadAnalysisSummaryById(summaryId: string) {
        return this.http.get(`${this.apiUrl}/download-analysis-summary/${summaryId}`, {
            responseType: 'blob',
            observe: 'response',
        });
    }

    /**
     * Delete an analysis summary (DELETE /delete-analysis-summary/:summaryId).
     */
    deleteAnalysisSummary(summaryId: string) {
        return this.delete<{ success?: boolean }>(`${this.apiUrl}/delete-analysis-summary/${summaryId}`);
    }

    /**
     * Fetch a single analysis summary's markdown content for the History "View" action
     * (GET /analysis-summary-content/:summaryId).
     */
    getAnalysisSummaryContentById(summaryId: string) {
        return this.get<GeotechnicalSummaryContent>(`${this.apiUrl}/analysis-summary-content/${summaryId}`);
    }

    /**
     * Rename an analysis summary history entry (PUT /rename-analysis-summary/:summaryId).
     */
    renameAnalysisSummary(summaryId: string, name: string) {
        return this.put<GeotechnicalAnalysisSummaryListItem>(`${this.apiUrl}/rename-analysis-summary/${summaryId}`, { name });
    }

    /**
     * Restore an older analysis summary as the current one (POST /restore-analysis-summary/:summaryId).
     * Returns the restored summary's content so the main view can refresh.
     */
    restoreAnalysisSummary(summaryId: string) {
        return this.post<GeotechnicalSummaryContent>(`${this.apiUrl}/restore-analysis-summary/${summaryId}`, {});
    }


    /**
     * Fetch formula dashboard for a geotechnical analysis (GET /:id/formula-dashboard).
     */
    getFormulaDashboard(analysisId: string) {
        return this.get<IGeotechnicalFormulaDashboard | null>(`${this.apiUrl}/${analysisId}/formula-dashboard`);
    }

    updateAnalysisName(id: string, name: string) {
        return this.put<GeotechnicalAnalysisReport>(`${this.apiUrl}/${id}`, { data: JSON.stringify({ name }) });
    }

    updateAnalysisDueDate(id: string, dueDate: Date | null) {
        return this.put(`${this.apiUrl}/${id}`, { data: JSON.stringify({ dueDate: dueDate ? dueDate.toISOString() : null }) });
    }

    getValueEngineering(analysisId: string) {
        return this.get<IGeotechnicalValueEngineering>(`${this.apiUrl}/${analysisId}/value-engineering`);
    }

    /**
     * Fetch vendor analysis for a geotechnical analysis (GET /:id/vendor-analysis).
     */
    getVendorAnalysis(analysisId: string) {
        return this.get<IGeotechnicalVendorAnalysis>(`${this.apiUrl}/${analysisId}/vendor-analysis`);
    }

    /**
     * Update project info fields and trigger vendor analysis reprocessing (PUT /:id/project-info).
     */
    updateProjectInfo(analysisId: string, data: {
        projectArea: number;
        shiftHoursPerDay: number;
        theoreticalBudget: number;
        theoreticalScheduleWorkdays: number;
        projectLocation?: string | null;
    }) {
        return this.put<{ success: boolean }>(`${this.apiUrl}/${analysisId}/project-info`, data);
    }

    /**
     * Fetch layer visualization data (GET /:id/layer-visualization).
     */
    getLayerVisualization(analysisId: string) {
        return this.get<ILayerVisualizationData>(`${this.apiUrl}/${analysisId}/layer-visualization`);
    }

    /**
     * Delete an analysis report (DELETE /delete-analysis-report).
     */
    deleteAnalysisReport(analysisId: string) {
        return this.delete<{ success?: boolean; message?: string }>(`${this.apiUrl}/delete-analysis-report`, { id: analysisId });
    }

    /**
     * Calculate costs for a new alternate based on current alternate + newly selected layer methods.
     * POST /:id/calculate-costs-for-new-alternate
     */
    calculateCostsForNewAlternate(analysisId: string, body: ICalculateCostsForNewAlternateRequest) {
        return this.post<IAlternative>(`${this.apiUrl}/${analysisId}/calculate-costs-for-new-alternate`, body);
    }

    /**
     * Generates Resource Cost Breakdown XLSX on the server, uploads it to S3, and
     * returns a short-lived signed URL for OnlyOffice preview or download.
     * POST /:id/export-resource-cost-breakdown
     */
    exportResourceCostBreakdown(analysisId: string) {
        return this.post<{ signedUrl: string }>(
            `${this.apiUrl}/${analysisId}/export-resource-cost-breakdown`,
            {}
        );
    }

    /** Generates a Work Cost Breakdown XLSX on the backend and returns a signed S3 URL. */
    exportWorkCostBreakdown(analysisId: string) {
        return this.post<{ signedUrl: string }>(
            `${this.apiUrl}/${analysisId}/export-work-cost-breakdown`,
            {}
        );
    }

    /** Generates an Impact vs. Alternate XLSX on the backend and returns a signed S3 URL. */
    exportImpactVsAlternate(analysisId: string) {
        return this.post<{ signedUrl: string }>(
            `${this.apiUrl}/${analysisId}/export-impact-vs-alternate`,
            {}
        );
    }

    /** Generates an Alternative Analysis Results XLSX on the backend and returns a signed S3 URL. */
    exportAlternativeAnalysisResults(analysisId: string) {
        return this.post<{ signedUrl: string }>(
            `${this.apiUrl}/${analysisId}/export-alternative-analysis-results`,
            {}
        );
    }

    /**
     * Fetch alternate leaderboard data (GET /:id/alternate-leaderboard).
     * Optional filters: layers (comma-separated), rankBy ('cost'|'schedule'), topN (number).
     */
    getAlternateLeaderboard(analysisId: string, filters?: ILeaderboardFilters) {
        const params: Record<string, string> = {};
        if (filters?.rankBy) params['rankBy'] = filters.rankBy;
        if (filters?.topN != null) params['topN'] = String(filters.topN);
        if (filters?.layers?.length) params['layers'] = filters.layers.join(',');
        return this.get<IAlternateLeaderboardResponse>(`${this.apiUrl}/${analysisId}/alternate-leaderboard`, params);
    }

    getDashboardFilters(analysisId: string) {
        return this.get<IDashboardFilters | null>(`${this.apiUrl}/${analysisId}/dashboard-filters`);
    }

    saveDashboardFilters(analysisId: string, filters: IDashboardFilters) {
        return this.put<IDashboardFilters>(`${this.apiUrl}/${analysisId}/dashboard-filters`, filters);
    }

    updateAlternativeKey(analysisId: string, oldKey: string, newKey: string) {
        return this.put<IGeotechnicalValueEngineering>(
            `${this.apiUrl}/${analysisId}/value-engineering/alternative-key`,
            { oldKey, newKey }
        );
    }

    /**
     * Download a sample file (GET /download-sample?type=EXAMPLE_EXCEL|STANDARD_EXCEL|EXAMPLE_FORMULA|STANDARD_FORMULA). Returns buffer.
     */
    downloadSample(type: GEOTECHNICAL_SAMPLE_DOWNLOAD_TYPE) {
        return this.get<{ data: Buffer }>(`${this.apiUrl}/download-sample`, { type });
    }

    /**
     * Fetch the persisted AI Dashboard for an analysis (GET /:id/ai-dashboard).
     * Returns status, a time-limited signed URL to download the HTML (if generated), and any error.
     * Use on component init to restore a previously generated dashboard after refresh.
     */
    getAiDashboard(analysisId: string) {
        return this.get<{
            status: GEOTECHNICAL_AI_DASHBOARD_STATUS;
            signedUrl: string | null;
            error: string | null;
            /** Last per-request instructions the user provided when triggering generation. */
            lastUserInstructions: string | null;
            /** Workspace-level AI Dashboard instructions from the configuration drawer. */
            workspaceInstructions: string | null;
        }>(`${this.apiUrl}/${analysisId}/ai-dashboard`);
    }

    /**
     * Trigger AI dashboard HTML generation for the given analysis
     * (POST /:id/generate-ai-dashboard).
     *
     * The result is delivered asynchronously via the
     * GEOTECHNICAL_AI_DASHBOARD_STATUS_CHANGED socket event.
     */
    /**
     * Trigger AI dashboard HTML generation for the given analysis.
     * Optional `userInstructions` are forwarded to the Python agent and appended
     * to the LLM prompt so the generated dashboard respects them.
     */
    generateAiDashboard(analysisId: string, userInstructions?: string) {
        return this.post<{ success: boolean; message: string }>(
            `${this.apiUrl}/${analysisId}/generate-ai-dashboard`,
            userInstructions?.trim() ? { userInstructions } : {}
        );
    }

    /**
     * List all AI dashboard versions for an analysis (GET /:id/ai-dashboard/versions).
     * Returned in reverse-chronological order; createdBy is populated.
     */
    aiDashboardVersionList(analysisId: string) {
        return this.get<IAiDashboardVersion[]>(`${this.apiUrl}/${analysisId}/ai-dashboard/versions`);
    }

    /**
     * Rename an AI dashboard version (PUT /:id/ai-dashboard/versions/:versionId).
     */
    renameAiDashboardVersion(analysisId: string, versionId: string, name: string) {
        return this.put<IAiDashboardVersion>(
            `${this.apiUrl}/${analysisId}/ai-dashboard/versions/${versionId}`,
            { name }
        );
    }

    /**
     * Restore an AI dashboard version as the current dashboard
     * (POST /:id/ai-dashboard/versions/:versionId/restore).
     * Backend updates GeotechnicalAnalysis.aiDashboardAttachment and aiDashboardLastUserInstructions.
     * Returns the signed URL and userInstructions of the restored version.
     */
    restoreAiDashboardVersion(analysisId: string, versionId: string) {
        return this.post<{ signedUrl: string | null; userInstructions: string | null }>(
            `${this.apiUrl}/${analysisId}/ai-dashboard/versions/${versionId}/restore`,
            {}
        );
    }

    /**
     * Hard-delete an AI dashboard version (DELETE /:id/ai-dashboard/versions/:versionId).
     */
    deleteAiDashboardVersion(analysisId: string, versionId: string) {
        return this.delete<{ success: boolean }>(
            `${this.apiUrl}/${analysisId}/ai-dashboard/versions/${versionId}`
        );
    }

    /**
     * Get a time-limited signed S3 URL for a specific AI dashboard version
     * (GET /:id/ai-dashboard/versions/:versionId/signed-url).
     */
    getAiDashboardVersionSignedUrl(analysisId: string, versionId: string) {
        return this.get<{ signedUrl: string }>(
            `${this.apiUrl}/${analysisId}/ai-dashboard/versions/${versionId}/signed-url`
        );
    }

    /**
     * Import a Bob Chat–generated HTML attachment as the current AI dashboard.
     * Creates a new version snapshot. Only available to Owners and Editors.
     * POST /:id/ai-dashboard/import-from-chat
     */
    importChatHtmlAsDashboard(analysisId: string, attachmentId: string) {
        return this.post<{ success: boolean }>(
            `${this.apiUrl}/${analysisId}/ai-dashboard/import-from-chat`,
            { attachmentId }
        );
    }

    /**
     * Retrieve all workspace-level geotechnical configuration values.
     * Returns a map keyed by GEOTECHNICAL_CONFIGURATION_TYPE; missing types
     * come back with empty defaults from the server.
     */
    getConfigurations() {
        return this.get<GeotechnicalConfigurationsMap>(`${this.apiUrl}/configuration`);
    }

    /**
     * Upsert a single configuration row for the current workspace
     * (PUT /geotechnical-analysis/configuration).
     */
    updateConfiguration(type: GEOTECHNICAL_CONFIGURATION_TYPE, value: string) {
        return this.put<GeotechnicalConfigurationItem>(`${this.apiUrl}/configuration`, { type, value });
    }

}

// ─── AI Dashboard status ──────────────────────────────────────────────────────

/** Mirrors GEOTECHNICAL_AI_DASHBOARD_STATUS on the backend. */
export enum GEOTECHNICAL_AI_DASHBOARD_STATUS {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CREDITS_EXHAUSTED = 'CREDITS_EXHAUSTED',
}

// ─── Configuration types ──────────────────────────────────────────────────────

/** Enum values that match GEOTECHNICAL_CONFIGURATION_TYPE on the backend. */
export enum GEOTECHNICAL_CONFIGURATION_TYPE {
    AI_DASHBOARD = 'AI_DASHBOARD',
    SUMMARY_INSTRUCTIONS = 'SUMMARY_INSTRUCTIONS',
    ROUND_LAYER_THICKNESS = 'ROUND_LAYER_THICKNESS',
}

/** Shape of a single configuration entry returned by the API. */
export interface GeotechnicalConfigurationItem {
    type: GEOTECHNICAL_CONFIGURATION_TYPE;
    value: string;
    updatedAt: string | null;
}

/** Full map returned by GET /configuration — one entry per known type. */
export type GeotechnicalConfigurationsMap = Record<
    GEOTECHNICAL_CONFIGURATION_TYPE,
    { value: string; updatedAt: string | null }
>;
